<template>
  <div>
    <SetupPage
        page-label="Mock Exams"
        eyebrow="ASSESSMENT STRUCTURE"
        title="Mock exams"
        description="Schedule the mock examinations sat before the real exams, grouped by class and named for the examination window."
        toolbar-title="Registered mock exams"
        :meta="metaLabel"
        :loader="onloader"
    >
        <template slot="side">
            <div v-if="mockitems !== null" class="ws-stat">
                <strong>{{ filteredMocks.length }}</strong>
                <span v-if="filteredMocks.length === mocks.length">
                    {{ mocks.length === 1 ? "mock exam" : "mock exams" }}
                </span>
                <span v-else>
                    of {{ mocks.length }} {{ mocks.length === 1 ? "mock exam" : "mock exams" }}
                </span>
            </div>
            <button
                type="button"
                class="btn ws-primary-button"
                :disabled="!classes.length"
                :title="classes.length ? 'Add a mock exam' : 'Create a class first'"
                @click="openCreate"
            >
                <i class="fa fa-plus" aria-hidden="true"></i>
                <span>New mock</span>
            </button>
        </template>

        <template slot="toolbar">
            <label class="ws-scope">
                <span>Class</span>
                <select
                    v-model="selectedClassId"
                    :disabled="classesLoading || !classes.length"
                    aria-label="Class to filter mock exams by"
                >
                    <option v-if="classesLoading" :value="0" disabled>Loading classes…</option>
                    <option v-else :value="0">All classes</option>
                    <option v-for="item in classes" :key="item.Id" :value="item.Id">
                        {{ item.Sch_Class }}
                    </option>
                </select>
            </label>

            <div class="ws-search">
                <i class="fa fa-search" aria-hidden="true"></i>
                <input
                    v-model="searchval"
                    type="search"
                    placeholder="Search exam or class"
                    aria-label="Search mock exams by title or class"
                    :disabled="!mocks.length"
                />
                <button
                    v-if="searchval"
                    type="button"
                    class="ws-search__clear"
                    aria-label="Clear search"
                    @click="searchval = ''"
                >
                    <i class="fa fa-times" aria-hidden="true"></i>
                </button>
            </div>
        </template>

        <div v-if="classesLoading" class="ws-state">
            <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
            <strong>Loading classes</strong>
            <span>Fetching the classes available at your school.</span>
        </div>

        <div v-else-if="classesFailed" class="ws-state">
            <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
            <strong>Classes could not be loaded</strong>
            <span>Check your connection and try again.</span>
            <button type="button" class="btn ws-ghost-button" @click="retryClasses">
                <i class="fa fa-refresh" aria-hidden="true"></i>
                <span>Try again</span>
            </button>
        </div>

        <div v-else-if="!classes.length" class="ws-state">
            <i class="fa fa-layer-group" aria-hidden="true"></i>
            <strong>No classes available</strong>
            <span>Set up your classes before scheduling mock exams.</span>
            <router-link to="/school/academic-management/classes" class="btn ws-ghost-button">
                <i class="fa fa-arrow-right" aria-hidden="true"></i>
                <span>Manage classes</span>
            </router-link>
        </div>

        <div v-else-if="mocksLoading && mockitems === null" class="ws-state">
            <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
            <strong>Loading mock exams</strong>
            <span>Fetching the mock exams registered for your school.</span>
        </div>

        <div v-else-if="mocksFailed" class="ws-state">
            <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
            <strong>Mock exams could not be loaded</strong>
            <span>Try loading the registered mock exams again.</span>
            <button type="button" class="btn ws-ghost-button" @click="retryMocks">
                <i class="fa fa-refresh" aria-hidden="true"></i>
                <span>Try again</span>
            </button>
        </div>

        <div v-else-if="!mocks.length" class="ws-state">
            <i class="fa fa-file-alt" aria-hidden="true"></i>
            <strong>No mock exams yet</strong>
            <span>Register the first mock exam, for example “First mock” for JSS 1.</span>
            <button type="button" class="btn ws-ghost-button" @click="openCreate">
                <i class="fa fa-plus" aria-hidden="true"></i>
                <span>Add mock exam</span>
            </button>
        </div>

        <div v-else-if="!filteredMocks.length" class="ws-state">
            <i class="fa fa-search" aria-hidden="true"></i>
            <strong v-if="hasSearch">No mock exams match “{{ searchval.trim() }}”</strong>
            <strong v-else>No mock exams for {{ selectedClassName }}</strong>
            <span v-if="hasSearch">Try a different title, class or filter.</span>
            <span v-else>Register the first mock exam for this class.</span>
            <button type="button" class="btn ws-ghost-button" @click="clearFilters">
                Clear filters
            </button>
        </div>

        <div v-else class="ws-grid" :class="{ 'is-loading': mocksLoading }">
            <article
                v-for="(item, index) in filteredMocks"
                :key="rowKey(item, index)"
                class="ws-card"
            >
                <span class="ws-card__index">{{ index + 1 }}</span>
                <div class="ws-card__body">
                    <span class="ws-card__title">{{ item.ExamTitle || "Untitled mock exam" }}</span>
                    <span class="ws-card__subtitle">{{ item.Sch_Class || "No class" }}</span>
                </div>
                <div class="ws-card__actions">
                    <button
                        type="button"
                        class="ws-edit-button"
                        :aria-label="editLabel(item)"
                        @click="editMock(item)"
                    >
                        <i class="fa fa-pencil" aria-hidden="true"></i>
                        <span>Edit</span>
                    </button>
                </div>
            </article>
        </div>
    </SetupPage>

    <SetupDrawer
        :show.sync="formVisible"
        icon="fa-file-alt"
        :eyebrow="editingId ? 'UPDATE RECORD' : 'NEW RECORD'"
        :title="editingId ? 'Edit mock exam' : 'Add a mock exam'"
        :subtitle="drawerSubtitle"
        :saving="isSaving"
        :submit-label="editingId ? 'Save changes' : 'Create mock'"
        :submit-disabled="!classes.length"
        form-id="mock-form"
        @close="closeForm"
        @submit="submitMock"
    >
        <fieldset class="ws-fieldset">
            <legend>Mock exam details</legend>

            <div class="row">
                <div class="col-sm-6">
                    <div class="form-group">
                        <label for="mock-class">
                            Class <span class="ws-required">*</span>
                        </label>
                        <select
                            id="mock-class"
                            v-model="form.classId"
                            class="form-control"
                            :class="{ 'is-invalid': errors.classId }"
                            :disabled="isSaving || !classes.length"
                            @change="clearError('classId')"
                        >
                            <option :value="0">Select class</option>
                            <option v-for="item in classes" :key="item.Id" :value="item.Id">
                                {{ item.Sch_Class }}
                            </option>
                        </select>
                        <span v-if="errors.classId" class="ws-error" role="alert">
                            {{ errors.classId }}
                        </span>
                    </div>
                </div>

                <div class="col-sm-6">
                    <div class="form-group">
                        <label for="mock-title">
                            Mock title <span class="ws-required">*</span>
                        </label>
                        <input
                            id="mock-title"
                            v-model="form.mockTitle"
                            type="text"
                            class="form-control"
                            :class="{ 'is-invalid': errors.mockTitle }"
                            placeholder="e.g. First mock"
                            maxlength="80"
                            autocomplete="off"
                            :disabled="isSaving"
                            @input="clearError('mockTitle')"
                        />
                        <span v-if="errors.mockTitle" class="ws-error" role="alert">
                            {{ errors.mockTitle }}
                        </span>
                    </div>
                </div>
            </div>

            <p class="ws-fieldset__note">
                The class groups the mock exam with its students on the results sheet.
            </p>
        </fieldset>
    </SetupDrawer>
  </div>
</template>

<script>
import Alert from "@/mixins/Alert.mixins";
import SetupPage from "@/components/SetupPage";
import SetupDrawer from "@/components/SetupDrawer";

const EMPTY_FORM = () => ({
    classId: 0,
    mockTitle: ""
});

export default {
    name: "mock",
    mixins: [Alert],
    components: {
        SetupPage,
        SetupDrawer
    },
    data() {
        return {
            schclass: null,
            classesFailed: false,
            mockitems: null,
            mocksLoading: false,
            mocksFailed: false,
            requestSeq: 0,
            selectedClassId: 0,
            searchval: "",
            formVisible: false,
            isSaving: false,
            editingId: 0,
            form: EMPTY_FORM(),
            errors: {},
            onloader: "none"
        };
    },
    computed: {
        classes() {
            return Array.isArray(this.schclass) ? this.schclass : [];
        },
        classesLoading() {
            return this.schclass === null;
        },
        mocks() {
            return Array.isArray(this.mockitems) ? this.mockitems : [];
        },
        hasSearch() {
            return this.searchval.trim().length > 0;
        },
        selectedClassName() {
            const found = this.classes.find(
                item => String(item.Id) === String(this.selectedClassId)
            );
            return found ? found.Sch_Class : "";
        },
        formClassName() {
            const found = this.classes.find(
                item => String(item.Id) === String(this.form.classId)
            );
            return found ? found.Sch_Class : "";
        },
        filteredMocks() {
            const tokens = this.searchval
                .toLowerCase()
                .split(/\s+/)
                .filter(Boolean);

            return this.mocks.filter(item => {
                if (this.selectedClassId) {
                    const hasClassId =
                        item &&
                        item.ClassId !== undefined &&
                        item.ClassId !== null &&
                        item.ClassId !== "";
                    if (hasClassId) {
                        if (String(item.ClassId) !== String(this.selectedClassId)) {
                            return false;
                        }
                    } else if (
                        String((item && item.Sch_Class) || "").trim() !==
                        this.selectedClassName
                    ) {
                        return false;
                    }
                }

                if (!tokens.length) return true;

                const haystack = [item && item.ExamTitle, item && item.Sch_Class]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();
                return tokens.every(token => haystack.includes(token));
            });
        },
        metaLabel() {
            if (this.classesLoading) return "Loading classes…";
            if (this.classesFailed) return "Classes unavailable.";
            if (!this.classes.length) return "No classes available yet.";
            if (this.mocksLoading && this.mockitems === null) return "Loading mock exams…";
            if (this.mocksFailed) return "Mock exams unavailable.";

            const total = this.mocks.length;
            const scope = this.selectedClassId ? `${this.selectedClassName} · ` : "";
            if (this.hasSearch) {
                return `${scope}${this.filteredMocks.length} of ${total} shown`;
            }
            const count = this.selectedClassId ? this.filteredMocks.length : total;
            return `${scope}${count} ${count === 1 ? "mock exam" : "mock exams"}`;
        },
        drawerSubtitle() {
            return [this.formClassName, String(this.form.mockTitle || "").trim()]
                .filter(Boolean)
                .join(" · ");
        }
    },
    methods: {
        rowKey(item, index) {
            if (item && item.Id !== undefined && item.Id !== null && item.Id !== "") {
                return item.Id;
            }
            return `mock-${index}`;
        },
        editLabel(item) {
            const label = [item && item.ExamTitle, item && item.Sch_Class]
                .filter(Boolean)
                .join(" — ");
            return label ? `Edit ${label}` : "Edit mock exam";
        },
        errorMessage(error, fallback) {
            const data = error && error.response && error.response.data;
            if (typeof data === "string" && data.trim()) return data.trim();
            if (data && typeof data === "object") {
                return data.message || data.responseMessage || fallback;
            }
            if (error && error.message) return error.message;
            return fallback;
        },
        async loadClasses() {
            this.schclass = null;
            this.classesFailed = false;

            try {
                const resp = await this.$store.dispatch("getclass");
                this.schclass = Array.isArray(resp) ? resp : [];
            } catch (error) {
                this.schclass = [];
                this.classesFailed = true;
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to load the classes."),
                    ""
                );
            }
        },
        async loadMocks() {
            const sequence = ++this.requestSeq;
            this.mocksLoading = true;
            this.mocksFailed = false;

            try {
                const resp = await this.$store.dispatch("getmock");
                if (sequence !== this.requestSeq) return;
                this.mockitems = Array.isArray(resp) ? resp : [];
            } catch (error) {
                if (sequence !== this.requestSeq) return;
                this.mocksFailed = true;
                this.mockitems = [];
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to load the mock exams."),
                    ""
                );
            } finally {
                if (sequence === this.requestSeq) this.mocksLoading = false;
            }
        },
        retryClasses() {
            this.classesFailed = false;
            this.loadClasses();
        },
        retryMocks() {
            this.mocksFailed = false;
            this.mockitems = null;
            this.loadMocks();
        },
        clearFilters() {
            this.selectedClassId = 0;
            this.searchval = "";
        },
        resetForm() {
            this.editingId = 0;
            this.errors = {};
            this.form = EMPTY_FORM();
        },
        openCreate() {
            if (!this.classes.length) return;
            this.resetForm();
            this.form = {
                classId: this.selectedClassId || this.classes[0].Id,
                mockTitle: ""
            };
            this.formVisible = true;
        },
        classIdFor(resp, item) {
            if (
                resp &&
                resp.ClassId !== undefined &&
                resp.ClassId !== null &&
                resp.ClassId !== ""
            ) {
                return resp.ClassId;
            }
            if (
                item &&
                item.ClassId !== undefined &&
                item.ClassId !== null &&
                item.ClassId !== ""
            ) {
                return item.ClassId;
            }
            const byName = this.classes.find(
                entry => item && entry.Sch_Class === item.Sch_Class
            );
            return byName ? byName.Id : 0;
        },
        async editMock(item) {
            if (!item || item.Id === undefined || item.Id === null) return;
            this.onloader = "flex";
            try {
                const resp = await this.$store.dispatch("getmockbyid", item.Id);
                if (!resp) throw new Error("The selected mock exam could not be loaded.");

                this.resetForm();
                this.editingId = item.Id;
                this.form = {
                    classId: this.classIdFor(resp, item),
                    mockTitle: resp.ExamTitle || item.ExamTitle || ""
                };
                this.formVisible = true;
            } catch (error) {
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to load this mock exam."),
                    ""
                );
            } finally {
                this.onloader = "none";
            }
        },
        closeForm() {
            this.formVisible = false;
            this.resetForm();
        },
        clearError(field) {
            if (!this.errors[field]) return;
            const errors = { ...this.errors };
            delete errors[field];
            this.errors = errors;
        },
        validate() {
            const errors = {};

            const classId = this.form.classId;
            if (
                classId === 0 ||
                classId === "0" ||
                classId === "" ||
                classId === null ||
                classId === undefined
            ) {
                errors.classId = "Choose the class for this mock exam.";
            }

            if (!String(this.form.mockTitle || "").trim()) {
                errors.mockTitle = "Enter a title for this mock exam.";
            }

            this.errors = errors;

            if (Object.keys(errors).length) {
                const fieldIds = {
                    classId: "mock-class",
                    mockTitle: "mock-title"
                };
                const firstField = Object.keys(errors)[0];
                const element = document.getElementById(fieldIds[firstField]);
                if (element) element.focus();
            }

            return Object.keys(errors).length === 0;
        },
        async submitMock() {
            if (this.isSaving) return;
            if (!this.validate()) return;

            const isEdit = Boolean(this.editingId);
            const payload = {
                Id: this.editingId || 1,
                ClassId: this.form.classId,
                MockName: String(this.form.mockTitle || "").trim(),
                processop: isEdit ? "U" : "N",
                username: this.$store.state.auth.username,
                accesskey: this.$store.state.auth.accesskey
            };

            this.isSaving = true;
            this.onloader = "flex";
            try {
                const resp = await this.$store.dispatch("mockregistration", payload);

                if (resp && resp.responseCode && resp.responseCode !== "00") {
                    this.ShowAlertError(
                        resp.responseMessage || "The mock exam could not be saved.",
                        ""
                    );
                    return;
                }

                this.closeForm();
                await this.loadMocks();

                if (this.mocksFailed) {
                    this.ShowAlertWarning(
                        "Mock exam saved, but the updated list could not be loaded.",
                        ""
                    );
                } else {
                    this.ShowAlertSuccess(
                        isEdit
                            ? "Mock exam updated successfully."
                            : "Mock exam created successfully.",
                        ""
                    );
                }
            } catch (error) {
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to save this mock exam at the moment."),
                    ""
                );
            } finally {
                this.isSaving = false;
                this.onloader = "none";
            }
        }
    },
    mounted() {
        const userDetails = this.$store.getters["auth/_user"];
        if (!userDetails || userDetails.isSchoolStaff == false) {
            this.$store.dispatch("auth/Logout");
            this.$router.push("/login");
            return;
        }
        this.loadClasses();
        this.loadMocks();
    }
};
</script>
